import { defineEndpoints } from '../../../../config/define-endpoints';
import type { PaginatedRequestV2, PaginatedResponseV2 } from '../../../../config/research/v2';
import type { WithSameOriginProxy } from '../../../../config/type-helpers';
import { ExecutionShapeV2 } from '../../execution';
import type { ExpandedUserShape } from '../../models/expanded-shapes';
import type { TagShape } from '../../tags/tags.model';

import { StudioVisualizationConfig, StudioVisualizationType } from './visualization-config';
import { VisualizationExpandKeys } from './visualizations-api';

type VisualizationQueryShape = {
    id: number;
    user_id: number;
    last_execution_id_cache: number;
    title: string;
    slug: string;
    sql: string;
    created_at: number;
    updated_at: number;
    tag_ids: number[];
    tags: TagShape[];
    last_execution_cache: ExecutionShapeV2;
};

type VisualizationShapeV2<T extends StudioVisualizationType = any> = {
    id: number;
    query_id: number;
    user_id: number;
    type: T;
    title: string;
    subtitle: string;
    description: string;
    config: StudioVisualizationConfig<T>;
    obscured_screenshot_url: string;
    created_at: number;
    updated_at: number;
    tag_ids: number[];
    tags: TagShape[];
    query: VisualizationQueryShape;
    user: ExpandedUserShape;
};

type GetVisualizationRequestV2 = WithSameOriginProxy<{ id: number | string; expand?: VisualizationExpandKeys[] }>;
type GetVisualizationResponseV2<T extends StudioVisualizationType = any> = VisualizationShapeV2<T>;

type GetVisualizationsRequestV2 = PaginatedRequestV2<
    Pick<VisualizationShapeV2, 'updated_at' | 'created_at' | 'title' | 'type'> & { slug: string },
    {
        expand?: VisualizationExpandKeys[];
        filters?: {
            id?: number | string;
            user_id?: number | string;
            title?: string;
            type?: StudioVisualizationType[];
            tag_id?: (number | string)[];
        };
    }
>;
type GetVisualizationsResponseV2 = PaginatedResponseV2<VisualizationShapeV2>;

const STUDIO_VISUALIZATIONS_V2_ENDPOINTS = defineEndpoints(template => ({
    GET: {
        VISUALIZATION: template('/v2/studio/visualization/{id}'),
        VISUALIZATIONS: template('/v2/studio/visualization'),
    },
}));

export type {
    GetVisualizationRequestV2,
    GetVisualizationResponseV2,
    GetVisualizationsRequestV2,
    GetVisualizationsResponseV2,
};

export { STUDIO_VISUALIZATIONS_V2_ENDPOINTS };
